import { Link } from "react-router-dom";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const EFFECTIVE = "February 27, 2026";
const CONTACT = "info@unitylanguagegroup.com";

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Unity Language Group" className="h-9 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">Unity Language Group</div>
              <div className="text-xs text-slate-500">Terms of Service</div>
            </div>
          </Link>
          <Link to="/privacy" className="text-xs text-slate-500 hover:text-slate-900 underline">Privacy Policy →</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-1">Terms of Service</h1>
        <div className="text-xs text-slate-500 mb-8">Effective {EFFECTIVE}</div>

        <Prose>
          <p>
            These Terms of Service ("<b>Terms</b>") govern your access to and use of the
            services offered by Unity Language Group, LLC, a Maryland limited liability company
            ("<b>Unity</b>," "<b>we</b>," "<b>us</b>," or "<b>our</b>"), including our website,
            interpreter application, client engagement services, operations platform, and
            electronic communications (collectively, the "<b>Services</b>"). By using the
            Services you agree to these Terms. If you do not agree, do not use the Services.
          </p>

          <Section n="1" title="Who May Use the Services">
            <p>
              You must be at least 18 years old and legally able to enter into a binding contract.
              If you are using the Services on behalf of an organization, you represent that you
              are authorized to bind that organization to these Terms.
            </p>
          </Section>

          <Section n="2" title="Description of Services">
            <ul>
              <li><b>Language services for clients:</b> Unity provides in-person, over-the-phone (OPI), video-remote (VRI), and translation services in francophone and other languages for USCIS, medical, legal, and general professional engagements. Rates, service windows, and scheduling terms are specified in each client's separate Service Agreement.</li>
              <li><b>Interpreter roster:</b> Individuals may apply to join Unity's roster of independent contractor interpreters. Approval is at Unity's discretion and requires a signed 1099 Independent Contractor Agreement and a completed W-9.</li>
              <li><b>Operations platform:</b> Authorized administrators, coordinators, and accountants may access Unity's internal platform to manage engagements. Interpreters access a separate portal to view their own assignments and payouts.</li>
            </ul>
          </Section>

          <Section n="3" title="Interpreter Independent Contractor Relationship">
            <p>
              Interpreters engaged by Unity are <b>independent contractors</b>, not employees.
              Interpreters are responsible for their own income and self-employment taxes and receive
              Form 1099-NEC where required by law. Interpreters retain discretion over which
              assignments to accept and how to perform their work, subject to the professional
              standards Unity communicates. Nothing in these Terms creates an employment, partnership,
              agency, or joint venture relationship.
            </p>
          </Section>

          <Section n="4" title="Confidentiality">
            <p>
              All parties must hold Unity's, its clients', and its interpreters' non-public
              information in strict confidence. This includes USCIS receipt numbers and A-numbers,
              medical information, legal case content, rates, contact information, and business
              plans. Confidentiality obligations survive termination.
            </p>
          </Section>

          <Section n="5" title="Fees and Payment">
            <ul>
              <li>Clients agree to pay the fees specified on their invoices. Unless otherwise stated, invoices are due within the payment window shown on the invoice, typically 48 hours from issuance for scheduled assignments and Net 15 for post-service billing.</li>
              <li>Payments are processed by Stripe, Inc. ("<b>Stripe</b>"). By completing checkout you agree to Stripe's terms. Unity does not store your full card number or bank credentials.</li>
              <li>Unity may cancel or release an unpaid, unconfirmed booking after the payment window elapses.</li>
              <li>Unless required by law, fees paid for completed services are non-refundable.</li>
            </ul>
          </Section>

          <Section n="6" title="Electronic Signatures &amp; Communications">
            <p>
              You consent to receive electronic contracts and to sign them electronically. Typed
              names and drawn signatures captured through our online signing flow constitute legal
              signatures under the federal E-SIGN Act (15 U.S.C. §§ 7001 et seq.) and the Maryland
              Uniform Electronic Transactions Act (Md. Code Ann., Com. Law §§ 21-101 et seq.). You
              agree we may communicate operational matters with you by email at the address you
              provide, and by SMS if you opt in.
            </p>
          </Section>

          <Section n="7" title="SMS / Text-Message Terms">
            <p>
              By providing your mobile number and opting in to SMS communications you agree to receive
              text messages from Unity related to your assignments, appointments, invoices, and account.
              These messages are sent through Twilio Inc.
            </p>
            <ul>
              <li><b>Message frequency:</b> variable — typically 2 to 8 messages per month, depending on your assignment volume.</li>
              <li><b>Message and data rates may apply.</b> Rates are set by your mobile carrier; Unity does not charge for the SMS itself.</li>
              <li><b>Opt-out:</b> Reply <b>STOP</b> to any Unity SMS to opt out. You will receive a single confirmation message and no further SMS.</li>
              <li><b>Help:</b> Reply <b>HELP</b> for assistance, or email <a href={`mailto:${CONTACT}`} className="text-blue-700 underline">{CONTACT}</a>.</li>
              <li><b>Carriers not liable:</b> Wireless carriers (including AT&amp;T, T-Mobile, Verizon and others) are not liable for delayed or undelivered messages.</li>
              <li><b>Supported carriers:</b> Major U.S. carriers. Not available on all carriers or in all areas.</li>
              <li><b>Consent is not a condition of purchase.</b> You do not have to consent to SMS to use Unity's other services.</li>
              <li>Mobile information collected in the SMS opt-in flow will not be shared with third parties or affiliates for marketing or promotional purposes.</li>
            </ul>
          </Section>

          <Section n="8" title="Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use the Services for any unlawful purpose, or to infringe others' rights.</li>
              <li>Impersonate any person or misrepresent your affiliation.</li>
              <li>Attempt to access accounts, records, or systems you are not authorized to access.</li>
              <li>Introduce malware, scrape at scale, or interfere with the security or integrity of the Services.</li>
              <li>Record or disclose confidential session content, including PHI or immigration case content, except as required to perform the engagement.</li>
            </ul>
          </Section>

          <Section n="9" title="Intellectual Property">
            <p>
              Unity's name, logos, platform, and all associated content are our property or that of
              our licensors. You are granted a limited, non-exclusive, revocable license to use the
              Services solely for the purposes described in these Terms. You retain ownership of
              content you submit, and grant us the license necessary to operate the Services.
            </p>
          </Section>

          <Section n="10" title="Third-Party Services">
            <p>
              The Services integrate with third-party providers (Stripe, Resend, Google/Gmail,
              Twilio, MongoDB Atlas, and hosting infrastructure). Your use of those providers is
              governed by their own terms. Unity is not responsible for the availability, content,
              or acts of third-party services.
            </p>
          </Section>

          <Section n="11" title="Disclaimer of Warranties">
            <p className="uppercase text-xs">
              The services are provided "as is" and "as available" without warranties of any
              kind, whether express or implied, including implied warranties of merchantability,
              fitness for a particular purpose, non-infringement, and any warranty arising from
              course of dealing or trade usage. Unity does not warrant that the services will be
              uninterrupted, error-free, or free of harmful components.
            </p>
          </Section>

          <Section n="12" title="Limitation of Liability">
            <p className="uppercase text-xs">
              To the fullest extent permitted by law, Unity's aggregate liability arising out of or
              relating to the services or these terms shall not exceed the fees paid by you to Unity
              in the twelve (12) months preceding the event giving rise to the claim, or one hundred
              U.S. dollars ($100), whichever is greater. In no event shall Unity be liable for any
              indirect, incidental, special, consequential, punitive, or exemplary damages, or for
              lost profits, revenues, data, or goodwill, even if advised of the possibility of such
              damages.
            </p>
          </Section>

          <Section n="13" title="Indemnification">
            <p>
              You agree to indemnify, defend, and hold Unity, its members, employees, and agents
              harmless from any claim, liability, damages, and expenses (including reasonable
              attorneys' fees) arising out of your breach of these Terms, your misuse of the
              Services, or your violation of any applicable law.
            </p>
          </Section>

          <Section n="14" title="Term and Termination">
            <p>
              These Terms remain in effect while you use the Services. Unity may suspend or terminate
              access at any time for any reason, including breach of these Terms. Sections 4, 5, 8,
              9, 11, 12, 13, and 15 survive termination.
            </p>
          </Section>

          <Section n="15" title="Governing Law &amp; Dispute Resolution">
            <p>
              These Terms are governed by the laws of the State of Maryland, without regard to its
              conflict-of-laws principles. The state and federal courts located in Montgomery County,
              Maryland shall have exclusive jurisdiction over any dispute arising out of or relating
              to these Terms or the Services, and you consent to personal jurisdiction there.
              You and Unity each waive any right to a jury trial. The parties will attempt to
              resolve disputes through good-faith negotiation before resorting to litigation.
            </p>
          </Section>

          <Section n="16" title="Changes to These Terms">
            <p>
              We may modify these Terms from time to time. When we do, we will update the "Effective"
              date above and, for material changes, notify active users by email where practical.
              Your continued use of the Services after changes take effect constitutes acceptance
              of the revised Terms.
            </p>
          </Section>

          <Section n="17" title="Miscellaneous">
            <ul>
              <li>If any provision of these Terms is held unenforceable, the remaining provisions remain in full effect.</li>
              <li>Our failure to enforce a right or provision is not a waiver of that right or provision.</li>
              <li>You may not assign these Terms without our written consent; Unity may assign them freely in connection with a merger, acquisition, or sale of assets.</li>
              <li>These Terms, together with our Privacy Policy and any signed engagement agreements, are the entire agreement between you and Unity concerning the Services.</li>
            </ul>
          </Section>

          <Section n="18" title="Contact">
            <p>
              Unity Language Group, LLC<br />
              Maryland, USA<br />
              <a href={`mailto:${CONTACT}`} className="text-blue-700 underline">{CONTACT}</a>
            </p>
          </Section>
        </Prose>

        <PublicFooter />
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mt-8" data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-lg font-bold text-slate-900 mb-2">{n}. {title}</h2>
      <div className="space-y-3 text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Prose({ children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-sm text-slate-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
      {children}
    </div>
  );
}
